import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Image as ImageIcon,
  Plus,
  Check,
  AlertCircle,
  Star,
  BookOpen,
  Zap,
  Radio
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import '../../styles/CuratorCreateCollection.css';

const CuratorCreateCollection = () => {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get organization ID from URL params (from wizard flow)
  const organizationId = searchParams.get('org');
  
  const [gallery, setGallery] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Collection form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    access_control: 'Public',
    featured: false,
    type: 'collection',
    start_date: '',
    end_date: ''
  });

  // Collection usage type
  const [collectionType, setCollectionType] = useState(null); // null | 'collection' | 'exhibition' | 'curated'

  // Event form data
  const [eventData, setEventData] = useState({
    name: '',
    description: '',
    event_start: '',
    event_end: '',
    event_type: 'show',
    featured: false,
    capacity: '',
    ticket_price: '',
    tickets_available: '',
    ticket_sales_enabled: false,
    ticket_description: ''
  });

  useEffect(() => {
    if (galleryId) {
      loadGalleryInfo();
    }
  }, [galleryId]);

  // Load organization info if provided
  useEffect(() => {
    if (organizationId) {
      loadOrganization();
    }
  }, [organizationId]);

  const loadOrganization = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('organization_id, name, slug, logo')
        .eq('organization_id', parseInt(organizationId))
        .single();
      
      if (!error && data) {
        setOrganization(data);
      }
    } catch (err) {
      console.error('Error loading organization:', err);
    }
  };

  const loadGalleryInfo = async () => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // If organization ID is provided, verify user is a member
      if (organizationId) {
        const { data: memberCheck } = await supabase
          .from('organization_members')
          .select('role')
          .eq('user_uuid', user.id)
          .eq('organization_id', parseInt(organizationId))
          .eq('is_active', true)
          .single();

        if (!memberCheck) {
          setMessage({ type: 'error', text: 'You do not have access to this organization' });
          return;
        }
      }

      // Load gallery details
      const { data: galleryData, error: galleryError } = await supabase
        .from('galleries')
        .select('*')
        .eq('gallery_id', galleryId)
        .single();

      if (galleryError) throw galleryError;
      setGallery(galleryData);

    } catch (err) {
      console.error('Error loading gallery:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    try {
      setUploadingImage(true);
      setMessage({ type: '', text: '' });

      const uploadResult = await uploadToCloudinary(file, 'image', 'collections');
      const imageUrl = uploadResult.url;

      setFormData(prev => ({ ...prev, cover_image: imageUrl }));
      
      // If creating an event, use the same image
      if (createEvent) {
        setEventData(prev => ({ ...prev, cover_image: imageUrl }));
      }

      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
    } catch (err) {
      console.error('Error uploading image:', err);
      setMessage({ type: 'error', text: 'Failed to upload image. Please try again.' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Sync fields with event data if using exhibition type
    if (collectionType === 'exhibition') {
      if (field === 'title') {
        setEventData(prev => ({ ...prev, name: value }));
      } else if (field === 'description') {
        setEventData(prev => ({ ...prev, description: value }));
      }
    }
  };

  const handleEventChange = (field, value) => {
    setEventData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return false;
    }
    if (!formData.description.trim()) {
      setMessage({ type: 'error', text: 'Description is required' });
      return false;
    }
    // For exhibition, strictly check event start/end
    if (collectionType === 'exhibition') {
      if (!eventData.event_start) {
        setMessage({ type: 'error', text: 'Start date is required for exhibitions' });
        return false;
      }
      if (!eventData.event_end) {
        setMessage({ type: 'error', text: 'End date is required for exhibitions' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setMessage({ type: 'error', text: 'User not authenticated' });
        return;
      }

      // Create collection
      const { data: collectionData, error: collectionError } = await supabase
        .from('collections')
        .insert({
          uuid: user.id, // Use the authenticated user's UUID
          title: formData.title,
          description: formData.description,
          cover_image: formData.cover_image,
          access_control: formData.access_control,
          featured: formData.featured,
          type: formData.type,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          organization_id: organizationId ? parseInt(organizationId) : null
          // created_at and updated_at will be handled by database triggers
        })
        .select()
        .single();

      if (collectionError) throw collectionError;

      const collectionId = collectionData.collection_id;

      // Create gallery-collection mapping
      const { error: mappingError } = await supabase
        .from('gallery_collection_mapping')
        .insert({
          gallery_id: parseInt(galleryId),
          collection_id: collectionId
        });

      if (mappingError) throw mappingError;

      // Create event if exhibition type selected
      let eventId = null;
      if (collectionType === 'exhibition') {
        // Convert local datetime-local strings to UTC ISO strings
        const localDatetimeToUTC = (str) => {
          if (!str) return null;
          try { const d = new Date(str); return isNaN(d.getTime()) ? str : d.toISOString(); } catch { return str; }
        };

        const { data: eventDataResult, error: eventError } = await supabase
          .from('events')
          .insert({
            name: formData.title, // Use collection title
            description: formData.description, // Use collection description
            event_start: localDatetimeToUTC(eventData.event_start),
            event_end: localDatetimeToUTC(eventData.event_end),
            event_type: 'exhibition', // Default type
            featured: formData.featured,
            status: 'active',
            gallery_id: parseInt(galleryId),
            collection_id: collectionId,
            cover_image: formData.cover_image,
            capacity: null, // Deferred
            ticket_price: null, // Deferred
            tickets_available: null, // Deferred
            ticket_sales_enabled: false, // Deferred
            ticket_description: null, // Deferred
            venue_name: gallery.name,
            venue_address: gallery.address,
            timezone: 'America/Chicago'
          })
          .select()
          .single();

        if (eventError) throw eventError;
        eventId = eventDataResult.id;
      }

      const typeLabel = collectionType === 'exhibition' ? 'Collection & Exhibition' : 'Collection';
      setMessage({ 
        type: 'success', 
        text: `${typeLabel} created successfully!` 
      });

      // Navigate to the collection detail page after a short delay
      setTimeout(() => {
        navigate(`/curator/collections/${collectionId}`);
      }, 2000);

    } catch (err) {
      console.error('Error creating collection:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="curator-create-collection">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading gallery information...</p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="curator-create-collection">
        <div className="error-state">
          <h3>Gallery Not Found</h3>
          <p>The gallery you're trying to create a collection for doesn't exist or you don't have access.</p>
          <button onClick={() => navigate('/curator')} className="btn-primary">
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-create-collection">
      {/* Header */}
      <div className="create-header">
        <button 
          className="back-button"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div className="header-content">
          <h1>Create New Exhibition or Collection</h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#6c757d', fontSize: '0.9375rem' }}>
            {organization && (
              <>
                <span style={{ fontWeight: 500 }}>{organization.name}</span>
                <span>→</span>
              </>
            )}
            <span>{gallery.name}</span>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          <div className="message-content">
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            <span>{message.text}</span>
          </div>
          <button 
            className="message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="collection-form">
        {/* Step 1: Collection Type Selection */}
        {!collectionType ? (
          <div className="form-section collection-type-selector">
            <div className="type-selector-header">
              <h2>What are you creating?</h2>
              <p>Choose the format that best fits your needs</p>
            </div>

            <div className="type-options">
              <button
                type="button"
                className="type-option"
                onClick={() => setCollectionType('collection')}
              >
                <div className="type-icon">
                  <BookOpen size={32} />
                </div>
                <h3>Digital Collection</h3>
                <p>A group of artworks for online display, portfolios, or thematic organization. No physical event dates attached.</p>
                <div className="type-cta">Select</div>
              </button>

              <button
                type="button"
                className="type-option"
                onClick={() => setCollectionType('exhibition')}
              >
                <div className="type-icon exhibition">
                  <Zap size={32} />
                </div>
                <h3>Exhibition / Show</h3>
                <p>A scheduled event at {gallery.name}. Includes start dates, ticketing options, and a linked collection of artworks.</p>
                <div className="type-cta">Select</div>
              </button>
            </div>

            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/curator/galleries/${galleryId}`)}
              style={{ marginTop: '2rem', alignSelf: 'center' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            {/* Unified Compact Form */}
            <div className="form-section">
              <div className="section-header-with-back" style={{ marginBottom: '1.5rem' }}>
                <button
                  type="button"
                  className="back-type-button"
                  onClick={() => setCollectionType(null)}
                >
                  ← Change Type
                </button>
                <div>
                  <h2>{collectionType === 'exhibition' ? 'Exhibition Details' : 'Collection Details'}</h2>
                  <p className="collection-type-label">
                    Creating: <span>{collectionType === 'collection' ? 'Digital Collection' : 'Exhibition / Show'}</span>
                  </p>
                </div>
              </div>

              <div className="compact-form-grid" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem' }}>
                
                {/* Left Column: Image */}
                <div className="image-column">
                  <div className="image-upload-section" style={{ marginTop: 0 }}>
                    <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Cover Image</h3>
                    <div className="image-upload-area" style={{ aspectRatio: '1/1', height: 'auto' }}>
                      {formData.cover_image ? (
                        <div className="uploaded-image">
                          <img src={formData.cover_image} alt="Collection cover" style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
                          <div className="image-overlay">
                            <label className="upload-btn">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleImageUpload(file);
                                }}
                                style={{ display: 'none' }}
                              />
                              <span>
                                <ImageIcon size={20} />
                                Change
                              </span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <>
                          <input
                            id="cover-image-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file);
                            }}
                            style={{ display: 'none' }}
                          />
                          <div 
                            className="upload-placeholder"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const input = document.getElementById('cover-image-input');
                              if (input) {
                                input.click();
                              }
                            }}
                            style={{ cursor: 'pointer', padding: '1rem' }}
                          >
                            {uploadingImage ? (
                              <div className="uploading">
                                <div className="loading-spinner"></div>
                                <p>Uploading...</p>
                              </div>
                            ) : (
                              <div>
                                <ImageIcon size={32} />
                                <p style={{ fontSize: '0.9rem' }}>Upload Cover</p>
                                <span style={{ fontSize: '0.75rem' }}>Square, min 800px</span>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column: Fields */}
                <div className="fields-column" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  
                  <div className="form-field">
                    <label htmlFor="title">{collectionType === 'exhibition' ? 'Exhibition Title *' : 'Collection Title *'}</label>
                    <input
                      id="title"
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleFormChange('title', e.target.value)}
                      placeholder={collectionType === 'exhibition' ? "e.g. Summer Showcase 2024" : "Enter collection title"}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="description">Description *</label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      placeholder="Describe this collection or exhibition..."
                      rows={collectionType === 'exhibition' ? 3 : 6}
                      required
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  {/* Exhibition Specific Fields */}
                  {collectionType === 'exhibition' && (
                    <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-field">
                        <label htmlFor="event_start">Event Start *</label>
                        <input
                          id="event_start"
                          type="datetime-local"
                          value={eventData.event_start}
                          onChange={(e) => handleEventChange('event_start', e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-field">
                        <label htmlFor="event_end">Event End *</label>
                        <input
                          id="event_end"
                          type="datetime-local"
                          value={eventData.event_end}
                          onChange={(e) => handleEventChange('event_end', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-field checkbox-field" style={{ marginTop: '0.5rem' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.featured}
                        onChange={(e) => handleFormChange('featured', e.target.checked)}
                      />
                      <Star size={16} />
                      Featured on Profile
                    </label>
                  </div>

                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="form-actions" style={{ marginTop: '1rem', borderTop: 'none', paddingTop: 0 }}>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => navigate(`/curator/galleries/${galleryId}`)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={saving || uploadingImage}
              >
                {saving ? (
                  <>
                    <div className="loading-spinner small"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Create {collectionType === 'exhibition' ? 'Exhibition' : 'Collection'}
                  </>
                )}
              </button>
            </div>
          </>

        )}
      </form>
    </div>
  );
};

export default CuratorCreateCollection;